import { OPENING_CONFIGURATION_TYPE } from 'app/typings/core/admin/experiences/enum/openingConfiguration';
import type { ExperienceResponse } from 'app/typings/core/admin/experiences/experience';
import type {
  CreateExperienceRequest,
  UpdateExperienceRequest,
} from 'app/typings/core/admin/experiences/experienceForm';
import type {
  Opening,
  OpeningHours,
  TimeRange,
} from 'app/typings/core/admin/experiences/openingHours/openingHours';
import { OpeningHoursType } from 'app/typings/core/admin/experiences/openingHours/openingHours';
import type { PriceConfigurationType } from 'app/typings/core/common/openingConfiguration/priceConfigurationType';
import {
  PRICE_COST_TYPE,
  PRICE_DIFFERENTIATION_MODE,
} from 'app/typings/core/common/openingConfiguration/priceConfigurationType';
import type {
  DayOfWeekStockConfiguration,
  StockConfigurationType,
} from 'app/typings/core/common/openingConfiguration/stockConfigurationType';
import { pickUpdatedFields } from 'app/utils/object';

export const mapExperienceToCreateRequest = (
  experience: Pick<
    ExperienceResponse,
    | 'hotelId'
    | 'countryId'
    | 'categoryId'
    | 'duration'
    | 'durationDescription'
    | 'time'
    | 'brand'
    | 'location'
    | 'emoji'
  >
): CreateExperienceRequest => ({
  hotelId: experience.hotelId,
  countryId: experience.countryId,
  categoryId: experience.categoryId,
  duration: experience.duration,
  durationDescription: experience.durationDescription,
  time: experience.time,
  brand: experience.brand,
  location: experience.location,
  emoji: experience.emoji,
});

const patchValue = <T>(value: T | null | undefined): T | null | undefined => {
  if (value === undefined) {
    return undefined;
  }

  if (value === null) {
    return null;
  }

  return value;
};

const mapStockConfiguration = (
  stockConfiguration: StockConfigurationType | undefined,
  showInInventory?: boolean
): StockConfigurationType | undefined => {
  if (!stockConfiguration) {
    return stockConfiguration;
  }

  if (stockConfiguration.type === OPENING_CONFIGURATION_TYPE.CONSTANT) {
    if (!showInInventory || !stockConfiguration.configuration?.stock) {
      return {
        ...stockConfiguration,
        configuration: { stock: null },
      };
    }
  }

  if (stockConfiguration.type === OPENING_CONFIGURATION_TYPE.DAILY) {
    const mappedDays = Object.entries(stockConfiguration.days).reduce(
      (acc, [day, dayConfig]) => {
        if (!dayConfig) {
          acc[day as keyof DayOfWeekStockConfiguration] = dayConfig;

          return acc;
        }

        const updatedConfig = {
          isClosed: dayConfig.isClosed ?? false,
          stock: dayConfig.isClosed
            ? 0
            : dayConfig.stock !== undefined
            ? dayConfig.stock
            : null,
        };

        acc[day as keyof DayOfWeekStockConfiguration] = updatedConfig;

        return acc;
      },
      {} as DayOfWeekStockConfiguration
    );

    return {
      ...stockConfiguration,
      days: mappedDays,
    };
  }

  return stockConfiguration;
};

const mapPriceConfiguration = (
  priceConfiguration: PriceConfigurationType | undefined,
  isAlwaysIncludedWithNight?: boolean
): PriceConfigurationType | undefined => {
  if (!priceConfiguration) {
    return priceConfiguration;
  }

  if (priceConfiguration.cost === PRICE_COST_TYPE.FREE) {
    return {
      ...priceConfiguration,
      cost: undefined,
      priceDifferentiationMode: isAlwaysIncludedWithNight
        ? PRICE_DIFFERENTIATION_MODE.WITH_DAYTIME
        : PRICE_DIFFERENTIATION_MODE.NONE,
      type: OPENING_CONFIGURATION_TYPE.CONSTANT,
      configuration: isAlwaysIncludedWithNight
        ? { daytimePrice: 0 }
        : { price: 0 },
    };
  }

  return { ...priceConfiguration };
};

const mapOpeningConfiguration = (
  openingConfiguration: ExperienceResponse['openingConfiguration'],
  isAlwaysIncludedWithNight?: boolean,
  showInInventory?: boolean
): ExperienceResponse['openingConfiguration'] => {
  if (!openingConfiguration) {
    return openingConfiguration;
  }

  return {
    ...openingConfiguration,
    stockConfiguration: mapStockConfiguration(
      openingConfiguration.stockConfiguration,
      showInInventory
    ),
    priceConfiguration: mapPriceConfiguration(
      openingConfiguration.priceConfiguration,
      isAlwaysIncludedWithNight
    ),
  };
};

type RawOpeningHours = {
  type: string;
  isAllDay?: boolean;
  slots?: TimeRange[];
  days?: Record<string, Opening & { enabled?: boolean }>;
};

const mapOpeningHours = (
  openingHours: RawOpeningHours | null | undefined
): OpeningHours | null | undefined => {
  if (!openingHours) {
    return openingHours;
  }

  if (openingHours.type === OpeningHoursType.WEEKLY) {
    if (openingHours.isAllDay) {
      return {
        type: OpeningHoursType.WEEKLY,
        isAllDay: true,
      };
    }

    return {
      type: OpeningHoursType.WEEKLY,
      isAllDay: openingHours.isAllDay ?? false,
      slots: openingHours.slots ?? [],
    };
  }

  if (openingHours.type === OpeningHoursType.DAILY) {
    const filteredDays: Partial<
      Record<
        string,
        { isAllDay?: boolean; slots?: { start: string; end: string }[] }
      >
    > = {};

    Object.entries(openingHours.days ?? {}).forEach(
      ([day, dayOpening]: [string, Opening & { enabled?: boolean }]) => {
        if (dayOpening.isAllDay) {
          filteredDays[day] = {
            isAllDay: true,
          };
        } else {
          if (dayOpening.slots && dayOpening.slots.length) {
            filteredDays[day] = {
              isAllDay: false,
              slots: (dayOpening.slots || []).filter(
                (slot: TimeRange) => slot && slot.start && slot.end
              ),
            };
          }
        }
      }
    );

    return {
      type: OpeningHoursType.DAILY,
      days: filteredDays,
    } as OpeningHours;
  }

  const resolvedType = openingHours.days
    ? OpeningHoursType.DAILY
    : OpeningHoursType.WEEKLY;

  return mapOpeningHours({ ...openingHours, type: resolvedType });
};

const createExhaustiveFieldsList = <
  T extends Record<keyof UpdateExperienceRequest, true>
>(
  fields: T
): (keyof T)[] => Object.keys(fields) as (keyof T)[];

type ExperienceUpdateValues = Partial<
  Omit<ExperienceResponse, 'id' | 'createdAt' | 'updatedAt'>
>;

const buildUpdatePayload = (
  experience: ExperienceUpdateValues
): UpdateExperienceRequest => {
  const updateFields = createExhaustiveFieldsList({
    categoryId: true,
    description: true,
    duration: true,
    brand: true,
    openingHours: true,
    spaceIds: true,
    durationDescription: true,
    time: true,
    roomServicePrice: true,
    accessMode: true,
    closingPeriods: true,
    maxQuantity: true,
    availableDayLimits: true,
    isRoomServiceAvailable: true,
    isRoomServiceIncluded: true,
    location: true,
    closedDaysBeforeCheckIn: true,
    tagline: true,
    minPax: true,
    maxPax: true,
    allowedPaxTypes: true,
    isAlwaysIncludedWithNight: true,
    openingConfiguration: true,
    coverPicture: true,
    emoji: true,
    experiencePictures: true,
    menu: true,
    treatmentList: true,
    importantInformation: true,
    showInInventory: true,
    availabilityMode: true,
  });

  return Object.fromEntries(
    updateFields
      .filter((field) => field in experience)
      .map((field) => {
        switch (field) {
          case 'openingConfiguration':
            return [
              field,
              patchValue(
                mapOpeningConfiguration(
                  experience['openingConfiguration'],
                  experience['isAlwaysIncludedWithNight'],
                  experience['showInInventory']
                )
              ),
            ];

          case 'openingHours':
            return [
              field,
              patchValue(mapOpeningHours(experience['openingHours'])),
            ];

          default:
            return [field, patchValue(experience[field])];
        }
      })
  ) as UpdateExperienceRequest;
};

export const mapExperienceToUpdateRequest = (
  experience: ExperienceUpdateValues,
  initialExperience?: ExperienceUpdateValues
): Partial<UpdateExperienceRequest> => {
  const payload = buildUpdatePayload(experience);

  if (!initialExperience) {
    return payload;
  }

  return pickUpdatedFields(payload, buildUpdatePayload(initialExperience));
};
